import * as React from "react";
import { useMappedState } from "redux-react-hook";
import { Formik } from "formik";
import * as _ from "lodash";

import {
  FieldMultiSelect,
  FieldValidation
} from "src/library/components/Forms";
import type { IStore } from "src/core/shared/store";
import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";

import {
  IProduct,
  useProductsApi
} from "../api/products.api";

interface ProductMuliSelect {
  onProductsChange: (data: { selected: string, productSelected: string[]; fullProductsSelected: IProduct[]; }) => void;
  productFilters?: FilterParams<IProduct>;
  ProductPagination?: PaginatedParams<IProduct>,
  disabledOptions?: number[];
  isHidden?: (drop: IProduct) => boolean;
  debounceTime?: number
  singleSelection?: boolean;
  labelOverride?: string
  validation?: FieldValidation,
  preSelectedProducts?: IProduct[]
  disabled?: boolean
}

const defaultPagination: PaginatedParams<IProduct> = {
  page: 0,
  limit: 100,
  offset: 0,
  sort: [ [ "updatedAt", "DESC" ] ]
};

export const ProductMuliSelect: React.ReactFCWithChildren<ProductMuliSelect> = ({
  onProductsChange, productFilters, disabledOptions, isHidden, ProductPagination, debounceTime, singleSelection, labelOverride, validation, preSelectedProducts, disabled
}) => {
  const [ productState, setProductState ] = React.useState<Array<{ text: string; value: string }>>([]);
  const [ loadedProducts, setLoadedProducts ] = React.useState<boolean>(false);
  const { terminology } = useMappedState(({ global }: IStore) => ({ terminology: global.settings && global.settings.terminology }));
  const [ productsResponse, getProducts ] = useProductsApi("LIST_PRODUCTS");
  const [ allProductsSearched, setAllProductsSearched ] = React.useState<IProduct[]>([]);

  // On mount, get the drops
  React.useEffect(() => {
    if (!loadedProducts) {
      getProducts({
        pagination: ProductPagination || defaultPagination,
        filters: productFilters || {}
      });
    }
  }, [
    ProductPagination,
    getProducts,
    loadedProducts,
    productFilters
  ]);

  const defaultValues = React.useMemo(() => {
    if (preSelectedProducts) {
      const dropValues = preSelectedProducts.map(product => ({
        text: `(${product.partNumber}) ${product.description}`,
        value: product.id.toString()
      }));

      //populate data with all the new data ensuring uniqueness
      setProductState(prev => {
        const merged = [ ...prev, ...dropValues ];

        return _.uniqBy(merged, "value");
      });

      return preSelectedProducts?.map(prod => prod.id.toString());
    }

    return undefined;
  }, [ preSelectedProducts ]);

  //for passing into the multiSelect
  const handleSpecificSearch = React.useCallback((search: string | null) => {
    if (search) {
      //get fuzzy searched drops
      getProducts({
        pagination: {
          ...defaultPagination,
          limit: 50
        },
        //search for branch number and name
        filters: {
          ...productFilters,
          $or: [ { description: { $like: `%${search}%` } }, { partNumber: { $like: `%${search}%` } } ]
        }
      });
    } else {
      getProducts({
        pagination: ProductPagination || defaultPagination,
        filters: productFilters || {}
      });
    }
  }, [
    ProductPagination,
    getProducts,
    productFilters
  ]);

  // Set list of drops as drop options.
  React.useEffect(() => {
    if (productsResponse.data?.result) {
      let products = productsResponse.data.result;

      if (isHidden) {
        products = _.filter(products, isHidden);
      }

      const dropValues = products.map(product => ({
        text: `(${product.partNumber}) ${product.description}`,
        value: product.id.toString()
      }));

      //populate data with all the new data ensuring uniqueness
      setProductState(prev => {
        const merged = [ ...prev, ...dropValues ];

        return _.uniqBy(merged, "value");
      });

      //keeping tabs on all the drops we have found
      setAllProductsSearched(prev => {
        const merged = _.uniqBy([ ...prev, ...products ], "id");

        return merged;
      });
      setLoadedProducts(true);
    }
  }, [ isHidden, productsResponse.data ]);

  const label = React.useMemo(() => {
    if (!singleSelection) {
      return `Select ${terminology ? terminology.product.plural.text : "Products"}`;
    } else {
      return `Select ${terminology ? terminology.product.singular.text : "Product"}`;
    }
  }, [ singleSelection, terminology ]);

  return (
    <Formik
      initialValues={{ multipleProducts: defaultValues }}
      // dont submit as should be used in from above this
      onSubmit={() => null}
    >
      {({ values, handleChange }) => (
        <FieldMultiSelect
          values={values.multipleProducts}
          name="multipleProducts"
          handleChange={handleChange}
          label={labelOverride ? labelOverride : label}
          options={productState}
          disabledOptions={_.map(disabledOptions, d => d.toString())}
          validateOnChange={false}
          loading={productsResponse.pending}
          fieldLogic={(affected, oldSelected) => onProductsChange({
            selected: affected,
            productSelected: oldSelected,
            fullProductsSelected: _.compact(_.map(oldSelected, selected => _.find(allProductsSearched, c => c.id.toString() === selected)))
          })}
          handleSpecificSearch={handleSpecificSearch}
          debounceTime={debounceTime}
          singleSelection={singleSelection}
          validation={validation}
          disabled={disabled}
        />
      )}
    </Formik>
  );
};
